import type { SkinEntry } from "./types";

/**
 * A SKIN is a curated form + layout pairing — the character of the interface:
 * shape, density, texture, type treatment, motion. It carries no colour.
 *
 * IMPORTANT: a skin is a REGISTRY concept, not a fourth CSS layer. The document
 * still carries exactly three attributes — data-palette, data-form, data-layout
 * — and every stylesheet is still authored against those. A skin only decides
 * which form and layout are applied together, and which palettes are offered
 * alongside them. Zero CSS knows the word "skin".
 *
 * THE STANDARD FOR ADDING ONE (recorded in CLAUDE.md):
 * a skin earns its place with a structural difference nameable in one line —
 * never to reach a number. A converter is a door you pass through, not a place
 * you sit; every extra skin multiplies the surface each future feature must be
 * checked against, in exchange for a decision the user makes once.
 */
export const SKINS: readonly SkinEntry[] = [
  {
    id: "ledger",
    nameEn: "Ledger",
    nameAr: "دفتر",
    descEn: "A printed statement: hairline rules, rows fused into one block.",
    descAr: "كشف حساب مطبوع: خطوط شعرية وصفوف ملتحمة في كتلة واحدة.",
    form: "ledger",
    layout: "stack",
    defaultPalette: "paper",
  },
  {
    id: "board",
    nameEn: "Board",
    nameAr: "لوحة",
    descEn: "The mechanical board behind the counter. Densest, monospaced, motionless.",
    descAr: "اللوحة الميكانيكية خلف الشبّاك. الأكثف، مونوسبيس، بلا حركة.",
    form: "board",
    layout: "compact",
    defaultPalette: "phosphor",
  },
  {
    id: "counter",
    nameEn: "Counter",
    nameAr: "شبّاك",
    descEn: "Service-desk calm: separated cards with room to breathe.",
    descAr: "هدوء طاولة الخدمة: بطاقات منفصلة ومساحة للتنفّس.",
    form: "counter",
    layout: "stack",
    defaultPalette: "steel",
  },
  {
    id: "intaglio",
    nameEn: "Intaglio",
    nameAr: "نقش",
    descEn: "Banknote engraving: a double frame and fine crosshatch grain.",
    descAr: "حفر الأوراق النقدية: إطار مزدوج وحبيبات تشابك دقيقة.",
    form: "intaglio",
    layout: "stack",
    defaultPalette: "engraved",
  },
  {
    id: "slate",
    nameEn: "Slate",
    nameAr: "لوح",
    descEn: "Everything at a glance: two columns once the screen is wide enough.",
    descAr: "كل شيء بنظرة: عمودان متى اتّسعت الشاشة.",
    form: "slate",
    layout: "grid",
    defaultPalette: "steel",
  },
  {
    id: "receipt",
    nameEn: "Receipt",
    nameAr: "إيصال",
    descEn: "A narrow thermal slip: perforated rules, monospaced throughout.",
    descAr: "قصاصة حرارية ضيّقة: فواصل مثقّبة ومونوسبيس بالكامل.",
    form: "receipt",
    layout: "stack",
    defaultPalette: "thermal",
  },
];

export const DEFAULT_SKIN = "ledger";

export function findSkin(id: string): SkinEntry | undefined {
  return SKINS.find((s) => s.id === id);
}

export function isKnownSkin(id: string): boolean {
  return SKINS.some((s) => s.id === id);
}

/** The skin whose form+layout match an applied appearance, if any. */
export function matchSkin(appearance: {
  form: string;
  layout: string;
}): SkinEntry | undefined {
  return SKINS.find((s) => s.form === appearance.form && s.layout === appearance.layout);
}
